import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import HelpPageHeaderActions from '../../components/help/HelpPageHeaderActions';
import KnowledgeBaseArticleCard from '../../components/help/KnowledgeBaseArticleCard';
import {
  getKnowledgeArticlesForOrganizationType,
  getTopLevelKnowledgeArticlesForOrganizationType,
} from '../../constants/knowledgeBaseArticles';
import {
  knowledgeArticleMatchesSearch,
  localizeKnowledgeArticles,
} from '../../helpers/knowledge-base-localization.helper';
import { useHelpTypography } from '../../hooks/useHelpTypography';
import { helpI18n } from '../../i18n/help';

const KnowledgeBase: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('translation', { i18n: helpI18n });
  const { pageTitleSx, bodySx } = useHelpTypography();
  const { selectedOrganization } = useOrganizationStore();
  const [search, setSearch] = useState('');

  const organizationArticles = useMemo(() => {
    if (!selectedOrganization) return [];
    return getKnowledgeArticlesForOrganizationType(
      selectedOrganization.organization_type,
    );
  }, [selectedOrganization]);

  const topLevelArticles = useMemo(() => {
    if (!selectedOrganization) return [];
    return getTopLevelKnowledgeArticlesForOrganizationType(
      selectedOrganization.organization_type,
    );
  }, [selectedOrganization]);

  const filteredArticles = useMemo(() => {
    const query = search.trim();
    const source = query ? organizationArticles : topLevelArticles;

    return localizeKnowledgeArticles(
      source.filter((article) => knowledgeArticleMatchesSearch(article, query))
    );
  }, [search, organizationArticles, topLevelArticles, i18n.language]);

  if (!selectedOrganization) {
    return (
      <Box>
        <Typography color="text.secondary">{t('knowledgeBase.selectOrganization')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: '16px',
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: `${theme.palette.primary.main}14`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <MenuBookOutlinedIcon sx={{ fontSize: 26, color: 'primary.main' }} />
            </Box>
            <Typography component="h1" sx={{ ...pageTitleSx, color: 'text.primary', m: 0 }}>
              {t('knowledgeBase.title')}
            </Typography>
          </Box>
          <HelpPageHeaderActions organizationId={selectedOrganization.id} />
        </Box>

        <TextField
          size="small"
          placeholder={t('knowledgeBase.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: '100%',
            maxWidth: 420,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#F8FAFC',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#E2E8F0',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.22)' : '#CBD5E1',
              },
              '&.Mui-focused': {
                bgcolor: theme.palette.background.paper,
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}1F`,
                '& fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
            },
            '& input': {
              py: 1.1,
              ...bodySx,
              '&::placeholder': {
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.42)' : '#94A3B8',
                opacity: 1,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {filteredArticles.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            borderRadius: '16px',
            p: 4,
            textAlign: 'center',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ ...bodySx, color: 'text.secondary', mb: 1 }}>
            {t('knowledgeBase.noResultsTitle')}
          </Typography>
          <Typography sx={{ ...bodySx, fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('knowledgeBase.noResultsHint')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={article.id}>
              <KnowledgeBaseArticleCard
                articleId={article.id}
                title={article.title}
                viewDetailsLabel={t('knowledgeBase.viewDetails')}
                onClick={() => navigate(`/knowledge-base/${article.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default KnowledgeBase;
